'use client';
import { useStyle } from '@/classify';
import { useDropdown } from '@/common/hooks/useDropdown';
import { useWindowSize } from '@/common/hooks/useWindowSize';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { ChevronDown as ArrowDown, ChevronUp as ArrowUp } from 'react-feather';
import { useIntl } from 'react-intl';
import Icon from '../Icon';
import defaultClasses from './productSort.module.css';
import SortItem from './sortItem';
const sortQueryMap = {
  created_at: 'new',
  position: 'featured',
  lw_best_seller: 'lw_best_sellers',
  lw_most_reviews: 'lw_top_rated',
  lw_low_to_high: 'lw_price_low',
  lw_high_to_low: 'lw_price_high',
};

const ProductSort = props => {
  const classes = useStyle(defaultClasses, props.classes);
  const { availableSortMethods, sortState, sortApi } = props;

  const { elementRef, expanded, setExpanded } = useDropdown();
  const { formatMessage, locale } = useIntl();
  const { isMobile } = useIsMobile();
  const pathname = usePathname();
  const sortMethodsFromConfig = availableSortMethods
    ? availableSortMethods
        .map(method => {
          const { value, label } = method;

          if (value !== 'price') {
            return {
              id: `sortItem.${value}`,
              text: label,
              attribute: value,
              sortDirection: value === 'low_to_high' ? 'ASC' : 'DESC',
            };
          }
        })
        .filter(method => !!method)
    : null;

  // click event for menu items
  const handleItemClick = useCallback(
    sortAttribute => {
      const searchParams = new URLSearchParams(location.search);

      searchParams.set('sort_by', sortQueryMap[sortAttribute.attribute]);

      globalThis.location.href = `${pathname}?${searchParams.toString()}`;
    },
    [pathname],
  );
  const sortTextRender = text => {
    if (text === 'Position') {
      return 'Featured';
    }

    return text;
  };
  const sortElements = useMemo(() => {
    // should be not render item in collapsed mode.
    if (!isMobile && !expanded) {
      return null;
    }

    const defaultSortMethods = [
      {
        id: 'sortItem.position',
        text: formatMessage({
          id: 'sortItem.position',
          defaultMessage: 'Position',
        }),
        attribute: 'position',
        sortDirection: 'DESC',
      },
      {
        id: 'sortItem.relevance',
        text: formatMessage({
          id: 'sortItem.relevance',
          defaultMessage: 'Best Match',
        }),
        attribute: 'relevance',
        sortDirection: 'DESC',
      },
      {
        id: 'sortItem.priceDesc',
        text: formatMessage({
          id: 'sortItem.priceDesc',
          defaultMessage: 'Price: High to Low',
        }),
        attribute: 'price',
        sortDirection: 'DESC',
      },
      {
        id: 'sortItem.priceAsc',
        text: formatMessage({
          id: 'sortItem.priceAsc',
          defaultMessage: 'Price: Low to High',
        }),
        attribute: 'price',
        sortDirection: 'ASC',
      },
    ];

    const allSortingMethods = sortMethodsFromConfig || defaultSortMethods;
    const field =
      allSortingMethods.find(
        method => method.attribute === sortState.sortAttribute && method.sortDirection === sortState.sortDirection,
      ) || allSortingMethods[0]; // 添加默认值
    // console.log('sortState', allSortingMethods, sortMethodsFromConfig, availableSortMethods);
    const itemElements = Array.from(allSortingMethods, sortItem => {
      const { attribute, sortDirection } = sortItem;
      const isActive = sortState.sortAttribute === attribute && sortState.sortDirection === sortDirection;

      const key = `${attribute}--${sortDirection}`;
      if (isMobile) {
        return (
          <div
            key={key}
            className='mb-[5px] flex h-[42px] items-center justify-start'
            onClick={() => handleItemClick(sortItem)}
          >
            <RadioGroupItem
              value={attribute}
              className={cn('h-[18px] w-[18px]')}
              radioClasses={cn(classes.menuItem, 'fill-[#000] text-[#000]')}
            ></RadioGroupItem>
            <span className={cn('ml-[10px] font-roboto-light text-[18px]')}>{sortTextRender(sortItem.text)}</span>
          </div>
        );
      }
      return (
        <li
          key={key}
          className={classes.menuItem}
        >
          <SortItem
            sortItem={sortItem}
            active={isActive}
            onClick={handleItemClick}
          />
        </li>
      );
    });
    if (isMobile) {
      return <RadioGroup value={field.attribute}>{itemElements}</RadioGroup>;
    }
    return (
      <div className={classes.menu}>
        <ul>{itemElements}</ul>
      </div>
    );
  }, [
    classes.menu,
    classes.menuItem,
    expanded,
    formatMessage,
    handleItemClick,
    sortMethodsFromConfig,
    sortState.sortAttribute,
    sortState.sortDirection,
  ]);

  // expand or collapse on click
  const handleSortClick = () => {
    setExpanded(!expanded);
  };

  const iconSrc = expanded ? ArrowUp : ArrowDown;

  const iconClass = expanded ? classes.desktopIconExpanded : classes.desktopIconCollapsed;

  const sortText = sort => {
    if (sort.sortText === 'Position') {
      return 'Featured';
    }

    return sort.sortText;
  };

  const sortButtonClass = expanded ? classes.sortButtonExpanded : classes.sortButtonCollapsed;

  const windowSize = useWindowSize();

  const isMobileAndTablet = windowSize.innerWidth < 960;
  if (isMobile) {
    return <div ref={elementRef}>{sortElements}</div>;
  }
  return (
    <div
      ref={elementRef}
      className={cn('relative ml-[30px] flex h-[50px] items-center  leading-[50px]', expanded ? '' : '')}
      aria-live='polite'
      aria-busy='false'
    >
      {/* <span className={classes.label}>
        <FormattedMessage
          id={"productSort.sortByButton"}
          defaultMessage={"Sort by"}
        />
      </span> */}
      <div
        className={sortButtonClass}
        onClick={handleSortClick}
      >
        <span className={cn(classes.desktopText, expanded ? 'border-b-2 border-solid border-[#E0E0E0] ' : '')}>
          {isMobileAndTablet ? (
            <span className={classes.sortContainer}>
              <span className={classes.sortTextShotMobile}>SORT</span>
              <span className={classes.sortTextMobile}>
                [{sortState.sortText === 'Position' ? 'Featured' : sortState.sortText}]
              </span>
            </span>
          ) : (
            <span className={cn(classes.sortText)}>
              Sort By:{sortState.sortText === 'Position' ? 'Featured' : sortState.sortText}
            </span>
          )}
          <Icon
            src={iconSrc}
            classes={{
              root: classes.desktopIconWrapper,
              icon: iconClass,
            }}
          />
        </span>
        {sortElements}
      </div>
    </div>
  );
};

export default ProductSort;
